import React from 'react';
import { usePermissions } from 'react-admin';
import { Grid } from '@material-ui/core';
import { Confirmation, DeleteButton } from '~/components';
import { TreeNode } from '~/types/Menu';
import { useNotify } from '~/hooks';
import { Alert } from '@material-ui/lab';
import { useTranslate } from 'react-admin';
import { useHistory } from 'react-router-dom';
import { useDeleteMenu, useDeleteItem } from '~/hooks/menu';
import { queryClient } from '~/App';

const l = (key: string): string => `resources.menu.delete.${key}`;

interface Props {
  selectedNode: TreeNode | null;
  selectedType: 'menu' | 'menuItem';
  menuId?: string;
  parentId?: string;
  loadChildren?: (id: string, forceReload?: boolean) => void;
  handleResetState?: () => void;
}

const DeleteAction = (props: Props) => {
  const { selectedNode, selectedType, menuId, parentId, loadChildren, handleResetState } = props;
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));
  const history = useHistory();
  const notify = useNotify();

  const deleteMenuMutation = useDeleteMenu({
    onSuccess: () => {
      history.push(`/menu?page=1&perPage=10`);
    },
  });

  const deleteItemMutation = useDeleteItem({
    onSuccess: () => {
      if (parentId && parentId !== menuId && loadChildren) {
        loadChildren?.(parentId, true);
      } else if (menuId) {
        queryClient.invalidateQueries({
          queryKey: ['menu', menuId, 'items', '$'],
        });
      }
    },
  });

  const onConfirm = () => {
    if (selectedNode?.id) {
      if (selectedType === 'menu') {
        return deleteMenuMutation.mutateAsync(selectedNode?.id).then(() => {
          notify({ message: t('success'), type: 'success' });
        });
      } else {
        return deleteItemMutation
          .mutateAsync({ menuId: menuId!, itemId: selectedNode?.id })
          .then(() => {
            if (handleResetState) {
              handleResetState();
            }
            notify({ message: t('successItem'), type: 'success' });
          });
      }
    }
  };

  if (!permissions?.includes('dynamicMenu.delete')) return null;
  return (
    <Confirmation
      onConfirm={onConfirm}
      confirmButtonProps={{
        loading: deleteMenuMutation.isLoading,
      }}
      confirmationSettings={{
        variant: 'modal',
        modal: {
          heading: t('attention'),
          content: (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity='warning'>
                  {selectedType === 'menu' ? t('alert') : t('alertItem')}
                </Alert>
              </Grid>
            </Grid>
          ),
        },
      }}
    >
      <DeleteButton />
    </Confirmation>
  );
};

export default DeleteAction;
